<template>
  <validation-observer ref="linkageManifestValidator">
    <v-form
      v-if="manifest"
    >
      <v-card
        flat
        class="rounded-card"
      >
        <!-- DXE起票担当者 -->
        <v-card-text class="pt-4">
          <v-row>
            <v-col cols="12" md="4">
              <validation-provider
                v-slot="{errors}"
                name="DXE起票担当者"
                rules="max:12"
              >
                <v-combobox
                  v-model="manifest.dxe_vote_staff"
                  :error-messages="errors"
                  :items="voteStaffs"
                  :return-object="false"
                  dense
                  label="DXE起票担当者"
                  hide-details="auto"
                  outlined
                  persistent-placeholder
                >
                  <template v-slot:label>
                    <span>DXE起票担当者</span>
                    <span class="ml-1">
                          ({{ manifest.dxe_vote_staff?.length || 0 }} / 12)
                        </span>
                  </template>
                  <template v-slot:counter></template>
                </v-combobox>
              </validation-provider>
            </v-col>
          </v-row>
        </v-card-text>
        <!-- /DXE起票担当者 -->

        <!-- 排出事業者 -->
        <v-card-title class="pb-0">
          <span style="color: #0074BA">排出事業者</span>
        </v-card-title>
        <v-card-text class="pt-0">
          <v-row>
            <v-col cols="12" md="6">
              <h3>{{ supplier.name }}</h3>
              <ul style="list-style: none" class="text-small">
                <li>
                  〒{{ supplier.zip_code }}&emsp;{{ supplier.address }}
                </li>
                <li>
                  電話番号: {{ supplier.phone_number || '-' }}
                </li>
              </ul>
            </v-col>
            <v-col cols="12" md="6">
              <h3>{{ supplierLocation.name }}</h3>
              <ul style="list-style: none" class="text-small">
                <li>
                  〒{{ supplierLocation.zip_code }}&emsp;{{ supplierLocation.address }}
                </li>
                <li>
                  電話番号: {{ supplierLocation.phone_number || '-' }}
                </li>
              </ul>
            </v-col>
          </v-row>
        </v-card-text>
        <!-- /排出事業者 -->

        <!-- 回収履歴 -->
        <v-row
          v-if="manifest && supplier"
          dense
          style="padding-left: 24px"
        >
          <v-col cols="12" md="6">
            <manifest-history
              :key="reloadKey"
              type-of-manifest="second_manifest"
              :supplierable-id="supplier.id"
              :supplierable-location-code="supplierLocation.code"
              @reflect:manifest="reflectHistoryCallback"
            />
          </v-col>
        </v-row>
        <!-- /回収履歴 -->

        <!-- 引渡し日 -->
        <v-card-title class="pb-0">
          <span style="color: #0074BA">引渡し日</span>
        </v-card-title>
        <v-card-text class="pt-0">
          <v-row>
            <v-col cols="12" md="3">
              <cs-date-picker
                :date.sync="manifest.collected_date"
                :minimum-date="collectedMinimumDate"
                :maximum-date="collectedMaximumDate"
                @dateChangedCallback="collectedDateChangedCallback"
              >
                <template v-slot="{ on, attrs }">
                  <validation-provider
                    v-slot="{ errors }"
                    :class="manifest.manifest_type !== 'dxe_form' ? 'requiredMark' : ''"
                    :rules="{ required: manifest.manifest_type !== 'dxe_form', rangeInDate: { manifest_type: manifest.manifest_type, min: collectedMinimumDate, max: collectedMaximumDate } }"
                    name="引渡し日"
                  >
                    <v-text-field
                      v-model="manifest.collected_date"
                      v-bind="attrs"
                      v-on="on"
                      :error-messages="errors"
                      clearable
                      hide-details="auto"
                      label="引渡し日"
                      dense
                      persistent-hint
                      persistent-placeholder
                      outlined
                      readonly
                    />
                  </validation-provider>
                </template>
              </cs-date-picker>
            </v-col>
          </v-row>
        </v-card-text>
        <!-- /引渡し日 -->

        <v-card-title class="pb-0">
          <span style="color: #0074BA">マニフェスト</span>
        </v-card-title>
        <v-card-text class="pt-0">
          <v-row>
            <v-col cols="12">
              <v-radio-group
                v-model="manifest.manifest_type"
                row
                dense
                hide-details="auto"
                class="mt-0"
                :disabled="manifest.is_emission"
              >
                <v-radio
                  v-for="n in modifyManifestTypes"
                  :disabled="!n.valid"
                  :key="n.value"
                  :label="n.text"
                  :value="n.value"
                >
                  <template v-slot:label>
                    <label
                      class="pa-1"
                      :class="{ 'radio-selected': manifest.manifest_type === n.value }"
                      :style="{ 'cursor': n.valid ? 'pointer' : 'not-allowed' }"
                    >
                      {{ n.text }}
                    </label>
                  </template>
                </v-radio>
              </v-radio-group>
            </v-col>
          </v-row>
        </v-card-text>

        <!-- 引渡担当者／登録担当者 -->
        <v-card-text class="py-0">
          <v-row>
            <v-col cols="12" md="4">
              <validation-provider
                v-slot="{ errors }"
                :class="manifest.manifest_type === 'dxe_form' ? 'requiredMark' : '' "
                :rules="{ required: manifest.manifest_type === 'dxe_form', max: 12 }"
                :vid="`master_deliver_staff`"
                name="引渡担当者"
              >
                <v-combobox
                  v-model="manifest.deliver_staff"
                  :error-messages="errors"
                  :items="handleUsers"
                  :return-object="false"
                  dense
                  label="引渡担当者"
                  hide-details="auto"
                  outlined
                  persistent-placeholder
                >
                  <template v-slot:label>
                    <span>引渡担当者</span>
                    <span class="ml-1">
                        ({{ manifest.deliver_staff?.length || 0 }} / 12)
                      </span>
                  </template>
                  <template v-slot:counter></template>
                </v-combobox>
              </validation-provider>
            </v-col>
            <v-col cols="12" md="4">
              <validation-provider
                v-slot="{ errors }"
                :vid="`master_register_staff`"
                name="登録担当者"
                rules="max:12"
              >
                <v-combobox
                  v-model="manifest.register_staff"
                  :error-messages="errors"
                  :items="handleUsers"
                  :return-object="false"
                  dense
                  label="登録担当者"
                  hide-details="auto"
                  outlined
                  persistent-placeholder
                >
                  <template v-slot:label>
                    <span>登録担当者</span>
                    <span class="ml-1">
                        ({{ manifest.register_staff?.length || 0 }} / 12)
                      </span>
                  </template>
                  <template v-slot:counter></template>
                </v-combobox>
              </validation-provider>
            </v-col>
          </v-row>
        </v-card-text>
        <!-- /引渡担当者／登録担当者 -->

        <!-- 運搬担当者／運搬方法／車両 -->
        <v-card-text>
          <v-row>
            <v-col cols="12" md="4">
              <validation-provider
                v-slot="{ errors }"
                :rules="{ max: 12 }"
                name="運搬担当者"
              >
                <v-combobox
                  v-model="manifest.transport_user_name"
                  :error-messages="errors"
                  :items="drivers"
                  :return-object="false"
                  clearable
                  dense
                  hide-details="auto"
                  item-value="text"
                  label="運搬担当者"
                  outlined
                  persistent-placeholder
                  @change="transportUserChangeEvent"
                >
                  <template v-slot:label>
                    <span>運搬担当者</span>
                    <span class="ml-1">
                          ({{ manifest.transport_user_name?.length || 0 }} / 12)
                        </span>
                  </template>
                  <template v-slot:counter></template>
                </v-combobox>
              </validation-provider>
            </v-col>
            <v-col cols="12" md="4">
              <validation-provider
                v-slot="{ errors }"
                name="運搬方法"
              >
                <v-select
                  v-model="manifest.transportation_id"
                  :items="transportations"
                  :error-messages="errors"
                  clearable
                  dense
                  hide-details="auto"
                  label="運搬方法"
                  outlined
                  persistent-placeholder
                ></v-select>
              </validation-provider>
            </v-col>
            <v-col cols="12" md="4">
              <validation-provider
                v-slot="{ errors }"
                name="車両番号"
                rules="max:15"
              >
                <v-combobox
                  v-model="manifest.vehicle_number"
                  :error-messages="errors"
                  :items="vehicles"
                  :return-object="false"
                  :clearable="true"
                  item-text="number"
                  item-value="number"
                  dense
                  label="車両番号"
                  hide-details="auto"
                  outlined
                  persistent-placeholder
                >
                  <template v-slot:label>
                    <span>車両番号</span>
                    <span class="ml-1">
                          ({{ manifest.vehicle_number?.length || 0 }} / 15)
                        </span>
                  </template>
                  <template v-slot:counter></template>
                </v-combobox>
              </validation-provider>
            </v-col>
          </v-row>
        </v-card-text>
        <!-- /運搬担当者／運搬方法／車両 -->

        <v-tabs background-color="blue-grey lighten-4">
          <v-tab>{{ manifest.item_name || '未設定' }}</v-tab>
        </v-tabs>

        <v-card-text class="pt-2">
          <!-- マニフェスト番号, 取扱品目, 廃棄物の名称 -->
          <v-row>
            <v-col
              v-if="manifest.manifest_type !== 'dxe_form'"
              cols="12"
              md="4"
            >
              <validation-provider
                v-slot="{ errors }"
                name="マニフェスト番号"
                :rules="{
                      numeric: (manifest.manifest_type !== 'dxe_form'),
                      length: (manifest.manifest_type !== 'dxe_form') ? 11 : false
                    }"
              >
                <v-text-field
                  v-model="manifest.manifest_no"
                  :error-messages="errors"
                  :disabled="manifest.manifest_type === 'dxe_form'"
                  :filled="manifest.manifest_type === 'dxe_form'"
                  dense
                  hide-details="auto"
                  label="マニフェスト番号"
                  outlined
                  persistent-placeholder
                >
                  <template v-slot:label>
                    <span>マニフェスト番号</span>
                    <span class="ml-1">
                          ({{ manifest.manifest_no?.length || 0 }} / 11)
                        </span>
                  </template>
                  <template v-slot:counter></template>
                </v-text-field>
              </validation-provider>
            </v-col>

            <v-col cols="12" md="4">
              <validation-provider>
                <validation-provider
                  v-slot="{ errors }"
                  class="requiredMark"
                  name="取扱品目"
                  rules="required"
                >
                  <item-autocomplete
                    v-model="manifest.item_id"
                    :items="items"
                    :error-messages="errors"
                    label="取扱品目"
                    clearable
                    dense
                    hide-details="auto"
                    outlined
                    persistent-placeholder
                    placeholder="品目名で検索する"
                    @change="itemChangeEvent"
                  />
                </validation-provider>
              </validation-provider>
            </v-col>

            <v-col cols="12" md="4">
              <validation-provider
                v-slot="{ errors }"
                name="廃棄物の名称"
                rules="max:30"
              >
                <v-text-field
                  v-model="manifest.waste_name"
                  :error-messages="errors"
                  dense
                  hide-details="auto"
                  label="廃棄物の名称"
                  outlined
                  persistent-placeholder
                >
                  <template v-slot:label>
                    <span>廃棄物の名称</span>
                    <span class="ml-1">
                          ({{ manifest.waste_name?.length || 0 }} / 30)
                        </span>
                  </template>
                  <template v-slot:counter></template>
                </v-text-field>
              </validation-provider>
            </v-col>

          </v-row>
          <!-- マニフェスト番号, 取扱品目, 廃棄物の名称 -->

          <!-- 数量, 数量単位, 数量の確定者-->
          <v-row>
            <v-col
              cols="12"
              md="2"
            >
              <validation-provider
                v-slot="{ errors }"
                :custom-messages="customMessages"
                :class="(manifest.manifest_type === 'dxe_form') ? ((manifest.quantity_confirm_id === 2) || (manifest.quantity_unit_id > 0) ? 'requiredMark' : '') : ((manifest.quantity_unit_id > 0) ? 'requiredMark' : '')"
                :rules="{ required: (((manifest.manifest_type === 'dxe_form') && ((manifest.quantity_confirm_id === 2) || manifest.quantity_unit_id > 0)) || ((manifest.manifest_type !== 'dxe_form') && manifest.quantity_unit_id > 0)), double: true, min_value: 0.001, max_value: 99999.999, quantityWithDecimal: { int_digits: 5, dec_digits: 3 } }"
                name="数量"
              >
                <v-text-field
                  v-model="manifest.quantity"
                  :error-messages="errors"
                  :show-size="1000"
                  dense
                  label="数量"
                  hide-details="auto"
                  outlined
                  persistent-placeholder
                  reverse
                  required
                  type="number"
                  @keydown.69.prevent
                />
              </validation-provider>
            </v-col>
            <v-col cols="12" md="4">
              <validation-provider
                v-slot="{ errors }"
                :class="(manifest.manifest_type === 'dxe_form') ? ((manifest.quantity_confirm_id === 2) || (manifest.quantity > 0) ? 'requiredMark' : '') : ((manifest.quantity > 0) ? 'requiredMark' : '')"
                :rules="{ required: (manifest.manifest_type === 'dxe_form') ? ((manifest.quantity_confirm_id === 2) || (manifest.quantity > 0)) : ((manifest.quantity > 0)) }"
                name="数量単位"
              >
                <v-select
                  v-model="manifest.quantity_unit_id"
                  :items="quantityUnits"
                  :error-messages="errors"
                  clearable
                  dense
                  hide-details="auto"
                  label="数量単位       "
                  outlined
                  persistent-placeholder
                ></v-select>
              </validation-provider>
            </v-col>
            <v-col cols="12" md="6">
              <validation-provider
                v-slot="{ errors }"
                :class="manifest.manifest_type === 'dxe_form' ? 'requiredMark' : ''"
                :rules="{ required: manifest.manifest_type === 'dxe_form' }"
                name="数量の確定者"
              >
                <v-select
                  v-model="manifest.quantity_confirm_id"
                  :items="quantityConfirms"
                  :error-messages="errors"
                  clearable
                  dense
                  hide-details="auto"
                  label="数量の確定者"
                  outlined
                  persistent-placeholder
                ></v-select>
              </validation-provider>
            </v-col>
          </v-row>
          <!-- 数量, 数量単位, 数量の確定者-->

          <!-- 荷姿, 荷姿の数量 -->
          <v-row>
            <v-col cols="12" md="4">
              <validation-provider
                v-slot="{ errors }"
                :class="manifest.manifest_type === 'dxe_form' ? 'requiredMark' : ''"
                :rules="{ required: manifest.manifest_type === 'dxe_form' }"
                name="荷姿"
              >
                <v-select
                  v-model="manifest.packing_id"
                  :items="packings"
                  :error-messages="errors"
                  clearable
                  dense
                  hide-details="auto"
                  label="荷姿"
                  outlined
                  persistent-placeholder
                ></v-select>
              </validation-provider>
            </v-col>
            <v-col cols="12" md="2">
              <validation-provider
                v-slot="{ errors }"
                :rules="{ numeric: true, min_value: 0, max_value: 99999 }"
                name="荷姿の数量"
              >
                <v-text-field
                  v-model="manifest.packing_quantity"
                  :error-messages="errors"
                  :show-size="1000"
                  dense
                  label="荷姿の数量"
                  hide-details="auto"
                  outlined
                  persistent-placeholder
                  reverse
                  required
                  type="number"
                  @keydown.69.prevent
                />
              </validation-provider>
            </v-col>
          </v-row>
          <!-- 荷姿, 荷姿の数量 -->

          <!-- 有害物質 -->
          <template v-if="!disableHazardousSubstance">
            <v-row>
              <v-col
                cols="12"
                md="6"
              >
                <validation-provider
                  v-slot="{ errors }"
                  :rules="{
                      duplicateKeys: { keys: [
                        manifest.hazardous_substance_2_id,
                        manifest.hazardous_substance_3_id,
                        manifest.hazardous_substance_4_id,
                        manifest.hazardous_substance_5_id,
                        manifest.hazardous_substance_6_id
                      ] }
                    }"
                  name="有害物質1"
                >
                  <v-select
                    v-model="manifest.hazardous_substance_1_id"
                    :disabled="disableHazardousSubstance"
                    :error-messages="errors"
                    :items="hazardousSubstances"
                    clearable
                    dense
                    hide-details="auto"
                    label="有害物質1"
                    outlined
                    persistent-placeholder
                  />
                </validation-provider>
              </v-col>
              <v-col
                cols="12"
                md="6"
              >
                <validation-provider
                  v-slot="{ errors }"
                  :rules="{
                      duplicateKeys: { keys: [
                        manifest.hazardous_substance_1_id,
                        manifest.hazardous_substance_3_id,
                        manifest.hazardous_substance_4_id,
                        manifest.hazardous_substance_5_id,
                        manifest.hazardous_substance_6_id
                      ] }
                    }"
                  name="有害物質2"
                >
                  <v-select
                    v-model="manifest.hazardous_substance_2_id"
                    :disabled="disableHazardousSubstance"
                    :error-messages="errors"
                    :items="hazardousSubstances"
                    clearable
                    dense
                    hide-details="auto"
                    label="有害物質2"
                    outlined
                    persistent-placeholder
                  />
                </validation-provider>
              </v-col>
            </v-row>
            <v-row>
              <v-col
                cols="12"
                md="6"
              >
                <validation-provider
                  v-slot="{ errors }"
                  :rules="{
                      duplicateKeys: { keys: [
                        manifest.hazardous_substance_1_id,
                        manifest.hazardous_substance_2_id,
                        manifest.hazardous_substance_4_id,
                        manifest.hazardous_substance_5_id,
                        manifest.hazardous_substance_6_id
                      ] }
                    }"
                  name="有害物質3"
                >
                  <v-select
                    v-model="manifest.hazardous_substance_3_id"
                    :disabled="disableHazardousSubstance"
                    :error-messages="errors"
                    :items="hazardousSubstances"
                    clearable
                    dense
                    hide-details="auto"
                    label="有害物質3"
                    outlined
                    persistent-placeholder
                  />
                </validation-provider>
              </v-col>
              <v-col
                cols="12"
                md="6"
              >
                <validation-provider
                  v-slot="{ errors }"
                  :rules="{
                      duplicateKeys: { keys: [
                        manifest.hazardous_substance_1_id,
                        manifest.hazardous_substance_2_id,
                        manifest.hazardous_substance_3_id,
                        manifest.hazardous_substance_5_id,
                        manifest.hazardous_substance_6_id
                      ] }
                    }"
                  name="有害物質4"
                >
                  <v-select
                    v-model="manifest.hazardous_substance_4_id"
                    :disabled="disableHazardousSubstance"
                    :error-messages="errors"
                    :items="hazardousSubstances"
                    clearable
                    dense
                    hide-details="auto"
                    label="有害物質4"
                    outlined
                    persistent-placeholder
                  />
                </validation-provider>
              </v-col>
            </v-row>
            <v-row>
              <v-col
                cols="12"
                md="6"
              >
                <validation-provider
                  v-slot="{ errors }"
                  :rules="{
                      duplicateKeys: { keys: [
                        manifest.hazardous_substance_1_id,
                        manifest.hazardous_substance_2_id,
                        manifest.hazardous_substance_3_id,
                        manifest.hazardous_substance_4_id,
                        manifest.hazardous_substance_6_id
                      ] }
                    }"
                  name="有害物質5"
                >
                  <v-select
                    v-model="manifest.hazardous_substance_5_id"
                    :disabled="disableHazardousSubstance"
                    :error-messages="errors"
                    :items="hazardousSubstances"
                    clearable
                    dense
                    hide-details="auto"
                    label="有害物質5"
                    outlined
                    persistent-placeholder
                  />
                </validation-provider>
              </v-col>
              <v-col
                cols="12"
                md="6"
              >
                <validation-provider
                  v-slot="{ errors }"
                  :rules="{
                      duplicateKeys: { keys: [
                        manifest.hazardous_substance_1_id,
                        manifest.hazardous_substance_2_id,
                        manifest.hazardous_substance_3_id,
                        manifest.hazardous_substance_4_id,
                        manifest.hazardous_substance_5_id
                      ] }
                    }"
                  name="有害物質6"
                >
                  <v-select
                    v-model="manifest.hazardous_substance_6_id"
                    :disabled="disableHazardousSubstance"
                    :error-messages="errors"
                    :items="hazardousSubstances"
                    clearable
                    dense
                    hide-details="auto"
                    label="有害物質6"
                    outlined
                    persistent-placeholder
                  />
                </validation-provider>
              </v-col>
            </v-row>
          </template>
          <!-- /有害物質 -->

        </v-card-text>

        <v-card-text>
          <v-row>
            <v-col cols="12" class="pb-0">
              <validation-provider
                v-slot="{ errors }"
                class="requiredMark"
                name="運搬ルート"
                :rules="{
                      required: true,
                      secondManifestSameDisposalCheck: {
                          level: this.manifest.level,
                          form_type: this.manifest.form_type,
                          supplier_subscriber_number: this.supplier.subscriber_number,
                          disposer_subscriber_number: this.manifest.disposer_subscriber_number,
                        }
                    }"
              >
                <v-input
                  v-model="manifest.transport_route_id"
                  :error-messages="errors"
                  dense
                  label="運搬ルート"
                  outlined
                  readonly
                  persistent-hint
                />
              </validation-provider>
            </v-col>
          </v-row>
          <v-row>
            <v-col cols="12" md="12">
              <div>
                <v-text-field
                  v-model="searchByKeyword"
                  append-icon="mdi-magnify"
                  clearable
                  dense
                  hide-details="auto"
                  label="絞り込み検索"
                  outlined
                  placeholder="ルート名・運搬先名・処分先名から検索"
                  persistent-placeholder
                  style="font-size: 90%"
                />
              </div>
            </v-col>
          </v-row>
          <v-row>
            <v-col cols="12">
              <v-data-table
                :headers="transportRoutesTableHeaders"
                :items="transportRoutes"
                :item-class="transportRouteRowBackground"
                :items-per-page="-1"
                :search="searchByKeyword"
                hide-default-footer
                single-select
                disable-sort
                fixed-header
                height="23vh"
                class="elevation-1"
                @click:row="transportRowClick"
              >
                <template v-slot:[`item.name`]="{ item }">
                  <span :title="item.name">{{ item.name | truncate(20) }}</span>
                </template>
              </v-data-table>
            </v-col>
          </v-row>

          <!-- ルート詳細 -->
          <transport-route-result
            :custom-mode="true"
            :form="manifest"
            :transport-route-id="manifest.transport_route_id"
          />
          <!-- /ルート詳細 -->

        </v-card-text>


        <!-- 運搬方法／処分方法 -->
        <v-card-text class="py-0">
          <v-row>
            <v-col cols="12" md="6">
              <validation-provider v-slot="{ errors }" name="処分方法">
                <v-select
                  v-model="manifest.disposal_method_id"
                  :items="disposalMethods"
                  :error-messages="errors"
                  clearable
                  dense
                  hide-details="auto"
                  label="処分方法"
                  outlined
                  persistent-placeholder
                />
              </validation-provider>
            </v-col>
          </v-row>
        </v-card-text>
        <!-- /運搬方法／処分方法 -->


        <!-- 連絡番号, 備考 -->
        <v-card-text class="py-0">
          <v-row>

            <v-col cols="12" md="6" class="pa-0">
              <v-row>
                <v-col cols="12">
                  <validation-provider
                    v-slot="{ errors }"
                    :vid="`master_contact_number_1`"
                    rules="contact_num_only|max:20"
                    name="連絡番号１"
                  >
                    <v-text-field
                      v-model="manifest.contact_number_1"
                      :error-messages="errors"
                      dense
                      label="連絡番号１"
                      hide-details="auto"
                      outlined
                      persistent-placeholder
                    >
                      <template v-slot:label>
                        <span>連絡番号１</span>
                        <span class="ml-1">
                          ({{ manifest.contact_number_1?.length || 0 }} / 20)
                        </span>
                      </template>
                      <template v-slot:counter></template>
                      <template v-slot:append-outer>
                        <v-btn
                          color="primary"
                          fab
                          x-small
                          elevation="0"
                          @click="addContactNumberClick"
                        >
                          <v-icon dark> mdi-plus </v-icon>
                        </v-btn>
                      </template>
                    </v-text-field>
                  </validation-provider>
                </v-col>
              </v-row>

              <v-expand-transition>
                <v-row v-if="displayContactNumber2">
                  <v-col cols="12">
                    <validation-provider
                      v-slot="{ errors }"
                      :vid="`master_contact_number_2`"
                      rules="contact_num_only|max:20"
                      name="連絡番号２"
                    >
                      <v-text-field
                        v-model="manifest.contact_number_2"
                        :error-messages="errors"
                        dense
                        label="連絡番号２"
                        hide-details="auto"
                        outlined
                        persistent-placeholder
                      >
                        <template v-slot:label>
                          <span>連絡番号２</span>
                          <span class="ml-1">
                            ({{ manifest.contact_number_2?.length || 0 }} / 20)
                          </span>
                        </template>
                        <template v-slot:counter></template>
                        <template v-slot:append-outer>
                          <v-btn
                            color="primary"
                            fab
                            x-small
                            elevation="0"
                            @click="addContactNumberClick"
                          >
                            <v-icon dark> mdi-plus </v-icon>
                          </v-btn>
                        </template>
                      </v-text-field>
                    </validation-provider>
                  </v-col>
                </v-row>
              </v-expand-transition>

              <v-expand-transition>
                <v-row v-if="displayContactNumber3">
                  <v-col cols="12">
                    <validation-provider
                      v-slot="{ errors }"
                      :vid="`master_contact_number_3`"
                      rules="contact_num_only|max:20"
                      name="連絡番号３"
                    >
                      <v-text-field
                        v-model="manifest.contact_number_3"
                        :error-messages="errors"
                        dense
                        label="連絡番号３"
                        hide-details="auto"
                        outlined
                        persistent-placeholder
                      >
                        <template v-slot:label>
                          <span>連絡番号３</span>
                          <span class="ml-1">
                            ({{ manifest.contact_number_3?.length || 0 }} / 20)
                          </span>
                        </template>
                        <template v-slot:counter></template>
                        <template v-slot:append-outer>
                          <v-btn
                            color="primary"
                            fab
                            x-small
                            elevation="0"
                            style="visibility: hidden"
                            @click="addContactNumberClick"
                          >
                            <v-icon dark> mdi-plus </v-icon>
                          </v-btn>
                        </template>
                      </v-text-field>
                    </validation-provider>
                  </v-col>
                </v-row>
              </v-expand-transition>
            </v-col>

            <v-col cols="12" md="6" class="pa-0">
              <v-row>
                <v-col cols="12">
                  <validation-provider
                    v-slot="{ errors }"
                    name="備考１"
                    rules="max:25"
                  >
                    <v-text-field
                      v-model="manifest.note_1"
                      :error-messages="errors"
                      dense
                      hide-details="auto"
                      label="備考１"
                      outlined
                      persistent-placeholder
                    >
                      <template v-slot:label>
                        <span>備考１</span>
                        <span class="ml-1">
                          ({{ manifest.note_1?.length || 0 }} / 25)
                        </span>
                      </template>
                      <template v-slot:counter></template>
                      <template v-slot:append-outer>
                        <v-btn
                          color="primary"
                          fab
                          x-small
                          elevation="0"
                          @click="addNoteClick"
                        >
                          <v-icon dark> mdi-plus </v-icon>
                        </v-btn>
                      </template>
                    </v-text-field>
                  </validation-provider>
                </v-col>
              </v-row>
              <v-expand-transition>
                <v-row v-if="displayNote2">
                  <v-col cols="12">
                    <validation-provider
                      v-slot="{ errors }"
                      name="備考２"
                      rules="max:25"
                    >
                      <v-text-field
                        v-model="manifest.note_2"
                        :error-messages="errors"
                        dense
                        hide-details="auto"
                        label="備考２"
                        outlined
                        persistent-placeholder
                      >
                        <template v-slot:label>
                          <span>備考２</span>
                          <span class="ml-1">
                            ({{ manifest.note_2?.length || 0 }} / 25)
                          </span>
                        </template>
                        <template v-slot:counter></template>
                        <template v-slot:append-outer>
                          <v-btn
                            color="primary"
                            fab
                            x-small
                            elevation="0"
                            @click="addNoteClick"
                          >
                            <v-icon dark> mdi-plus </v-icon>
                          </v-btn>
                        </template>
                      </v-text-field>
                    </validation-provider>
                  </v-col>
                </v-row>
              </v-expand-transition>

              <v-expand-transition>
                <v-row v-if="displayNote3">
                  <v-col cols="12">
                    <validation-provider
                      v-slot="{ errors }"
                      name="備考３"
                      rules="max:25"
                    >
                      <v-text-field
                        v-model="manifest.note_3"
                        :error-messages="errors"
                        dense
                        hide-details="auto"
                        label="備考３"
                        outlined
                        persistent-placeholder
                      >
                        <template v-slot:label>
                          <span>備考３</span>
                          <span class="ml-1">
                            ({{ manifest.note_3?.length || 0 }} / 25)
                          </span>
                        </template>
                        <template v-slot:counter></template>
                        <template v-slot:append-outer>
                          <v-btn
                            color="primary"
                            fab
                            x-small
                            elevation="0"
                            @click="addNoteClick"
                          >
                            <v-icon dark> mdi-plus </v-icon>
                          </v-btn>
                        </template>
                      </v-text-field>
                    </validation-provider>
                  </v-col>
                </v-row>
              </v-expand-transition>

              <v-expand-transition>
                <v-row v-if="displayNote4">
                  <v-col cols="12">
                    <validation-provider
                      v-slot="{ errors }"
                      name="備考４"
                      rules="max:25"
                    >
                      <v-text-field
                        v-model="manifest.note_4"
                        :error-messages="errors"
                        dense
                        hide-details="auto"
                        label="備考４"
                        outlined
                        persistent-placeholder
                      >
                        <template v-slot:label>
                          <span>備考４</span>
                          <span class="ml-1">
                            ({{ manifest.note_4?.length || 0 }} / 25)
                          </span>
                        </template>
                        <template v-slot:counter></template>
                        <template v-slot:append-outer>
                          <v-btn
                            color="primary"
                            fab
                            x-small
                            elevation="0"
                            @click="addNoteClick"
                          >
                            <v-icon dark> mdi-plus </v-icon>
                          </v-btn>
                        </template>
                      </v-text-field>
                    </validation-provider>
                  </v-col>
                </v-row>
              </v-expand-transition>

              <v-expand-transition>
                <v-row v-if="displayNote5">
                  <v-col cols="12">
                    <validation-provider
                      v-slot="{ errors }"
                      name="備考５"
                      rules="max:25"
                    >
                      <v-text-field
                        v-model="manifest.note_5"
                        :error-messages="errors"
                        dense
                        hide-details="auto"
                        label="備考５"
                        outlined
                        persistent-placeholder
                      >
                        <template v-slot:label>
                          <span>備考５</span>
                          <span class="ml-1">
                            ({{ manifest.note_5?.length || 0 }} / 25)
                          </span>
                        </template>
                        <template v-slot:counter></template>
                        <template v-slot:append-outer>
                          <v-btn
                            color="primary"
                            fab
                            x-small
                            elevation="0"
                            style="visibility: hidden"
                            @click="addNoteClick"
                          >
                            <v-icon dark> mdi-plus </v-icon>
                          </v-btn>
                        </template>
                      </v-text-field>
                    </validation-provider>
                  </v-col>
                </v-row>
              </v-expand-transition>
            </v-col>

          </v-row>
        </v-card-text>
        <!-- /連絡番号, 備考 -->

        <!-- カスタムラベル -->
        <template
          v-if="isJwMode"
        >
          <v-card-title>
            <span v-if="manifest.custom_label.use_title" style="color: #0074BA">{{ manifest.custom_label.name_title }}</span>
          </v-card-title>
          <v-card-text>
            <v-row>
              <v-col cols="12" md="6" v-if="manifest.custom_label.custom_labels1_use">
                <validation-provider
                  v-slot="{ errors }"
                  vid="custom_labels1"
                  :name="manifest.custom_label.custom_labels1_name != null ? manifest.custom_label.custom_labels1_name : 'ラベル①'"
                  :rules="{ required: manifest.custom_label.custom_labels1_required,
                              alpha_num_only: manifest.custom_label.custom_labels1_halfsize_alphanumeric,
                              max: manifest.custom_label.custom_labels1_maximum_characters != null ? manifest.custom_label.custom_labels1_maximum_characters : 255,
                              min: manifest.custom_label.custom_labels1_minimum_characters != null ? manifest.custom_label.custom_labels1_minimum_characters : 0
                    }"
                >
                  <v-text-field
                    v-model="manifest.manifest_custom_label.custom_labels_1"
                    :error-messages="errors"
                    dense
                    hide-details="auto"
                    :label="manifest.custom_label.custom_labels1_name != null ? manifest.custom_label.custom_labels1_name : 'ラベル①'"
                    outlined
                    persistent-placeholder
                  >
                    <template v-slot:label>
                      <span>{{ manifest.custom_label.custom_labels1_name != null ? manifest.custom_label.custom_labels1_name : 'ラベル①' }}</span>
                      <span class="ml-1">
                          ({{ manifest.manifest_custom_label.custom_labels_1?.length || 0 }}
                          / {{ manifest.custom_label.custom_labels1_maximum_characters != null ? manifest.custom_label.custom_labels1_maximum_characters : 255 }})
                        </span>
                    </template>
                    <template v-slot:counter></template>
                  </v-text-field>
                </validation-provider>
              </v-col>
              <v-col cols="12" md="6" v-if="manifest.custom_label.custom_labels2_use">
                <validation-provider
                  v-slot="{ errors }"
                  vid="custom_labels2"
                  :name="manifest.custom_label.custom_labels2_name != null ? manifest.custom_label.custom_labels2_name : 'ラベル②'"
                  :rules="{ required: manifest.custom_label.custom_labels2_required,
                      alpha_num_only: manifest.custom_label.custom_labels2_halfsize_alphanumeric,
                      max: manifest.custom_label.custom_labels2_maximum_characters != null ? manifest.custom_label.custom_labels2_maximum_characters : 255 ,
                      min: manifest.custom_label.custom_labels2_minimum_characters != null ? manifest.custom_label.custom_labels2_minimum_characters : 0
                      }"
                >
                  <v-text-field
                    v-model="manifest.manifest_custom_label.custom_labels_2"
                    :error-messages="errors"
                    dense
                    hide-details="auto"
                    :label="manifest.custom_label.custom_labels2_name != null ? manifest.custom_label.custom_labels2_name : 'ラベル②'"
                    outlined
                    persistent-placeholder
                  >
                    <template v-slot:label>
                      <span>{{ manifest.custom_label.custom_labels2_name != null ? manifest.custom_label.custom_labels2_name : 'ラベル②' }}</span>
                      <span class="ml-1">
                          ({{ manifest.manifest_custom_label.custom_labels_2?.length || 0 }}
                          / {{ manifest.custom_label.custom_labels2_maximum_characters != null ? manifest.custom_label.custom_labels2_maximum_characters : 255 }})
                        </span>
                    </template>
                    <template v-slot:counter></template>
                  </v-text-field>
                </validation-provider>
              </v-col>
              <v-col cols="12" md="6" v-if="manifest.custom_label.custom_labels3_use">
                <validation-provider
                  v-slot="{ errors }"
                  vid="custom_labels3"
                  :name="manifest.custom_label.custom_labels3_name != null ? manifest.custom_label.custom_labels3_name : 'ラベル③'"
                  :rules="{ required: manifest.custom_label.custom_labels3_required,
                      alpha_num_only: manifest.custom_label.custom_labels3_halfsize_alphanumeric,
                      max: manifest.custom_label.custom_labels3_maximum_characters != null ? manifest.custom_label.custom_labels3_maximum_characters : 255 ,
                      min: manifest.custom_label.custom_labels3_minimum_characters != null ? manifest.custom_label.custom_labels3_minimum_characters : 0
                      }"
                >
                  <v-text-field
                    v-model="manifest.manifest_custom_label.custom_labels_3"
                    :error-messages="errors"
                    dense
                    hide-details="auto"
                    :label="manifest.custom_label.custom_labels3_name != null ? manifest.custom_label.custom_labels3_name : 'ラベル③'"
                    outlined
                    persistent-placeholder
                  >
                    <template v-slot:label>
                      <span>{{ manifest.custom_label.custom_labels3_name != null ? manifest.custom_label.custom_labels3_name : 'ラベル③' }}</span>
                      <span class="ml-1">
                          ({{ manifest.manifest_custom_label.custom_labels_3?.length || 0 }}
                          / {{ manifest.custom_label.custom_labels3_maximum_characters != null ? manifest.custom_label.custom_labels3_maximum_characters : 255 }})
                        </span>
                    </template>
                    <template v-slot:counter></template>
                  </v-text-field>
                </validation-provider>
              </v-col>
              <v-col cols="12" md="6" v-if="manifest.custom_label.custom_labels4_use">
                <validation-provider
                  v-slot="{ errors }"
                  vid="custom_labels4"
                  :name="manifest.custom_label.custom_labels4_name != null ? manifest.custom_label.custom_labels4_name : 'ラベル④'"
                  :rules="{ required: manifest.custom_label.custom_labels4_required,
                      alpha_num_only: manifest.custom_label.custom_labels4_halfsize_alphanumeric,
                      max: manifest.custom_label.custom_labels4_maximum_characters != null ? manifest.custom_label.custom_labels4_maximum_characters : 255 ,
                      min: manifest.custom_label.custom_labels4_minimum_characters != null ? manifest.custom_label.custom_labels4_minimum_characters : 0
                      }"
                >
                  <v-text-field
                    v-model="manifest.manifest_custom_label.custom_labels_4"
                    :error-messages="errors"
                    dense
                    hide-details="auto"
                    :label="manifest.custom_label.custom_labels4_name != null ? manifest.custom_label.custom_labels4_name : 'ラベル④'"
                    outlined
                    persistent-placeholder
                  >
                    <template v-slot:label>
                      <span>{{ manifest.custom_label.custom_labels4_name != null ? manifest.custom_label.custom_labels4_name : 'ラベル④' }}</span>
                      <span class="ml-1">
                          ({{ manifest.manifest_custom_label.custom_labels_4?.length || 0 }}
                          / {{ manifest.custom_label.custom_labels4_maximum_characters != null ? manifest.custom_label.custom_labels4_maximum_characters : 255 }})
                        </span>
                    </template>
                    <template v-slot:counter></template>
                  </v-text-field>
                </validation-provider>
              </v-col>
              <v-col cols="12" md="6" v-if="manifest.custom_label.custom_labels5_use">
                <validation-provider
                  v-slot="{ errors }"
                  vid="custom_labels5"
                  :name="manifest.custom_label.custom_labels5_name != null ? manifest.custom_label.custom_labels5_name : 'ラベル⑤'"
                  :rules="{ required: manifest.custom_label.custom_labels5_required,
                      alpha_num_only: manifest.custom_label.custom_labels5_halfsize_alphanumeric,
                      max: manifest.custom_label.custom_labels5_maximum_characters != null ? manifest.custom_label.custom_labels5_maximum_characters : 255 ,
                      min: manifest.custom_label.custom_labels5_minimum_characters != null ? manifest.custom_label.custom_labels5_minimum_characters : 0
                      }"
                >
                  <v-text-field
                    v-model="manifest.manifest_custom_label.custom_labels_5"
                    :error-messages="errors"
                    dense
                    hide-details="auto"
                    :label="manifest.custom_label.custom_labels5_name != null ? manifest.custom_label.custom_labels5_name : 'ラベル⑤'"
                    outlined
                    persistent-placeholder
                  >
                    <template v-slot:label>
                      <span>{{ manifest.custom_label.custom_labels5_name != null ? manifest.custom_label.custom_labels5_name : 'ラベル⑤' }}</span>
                      <span class="ml-1">
                          ({{ manifest.manifest_custom_label.custom_labels_5?.length || 0 }}
                          / {{ manifest.custom_label.custom_labels5_maximum_characters != null ? manifest.custom_label.custom_labels5_maximum_characters : 255 }})
                        </span>
                    </template>
                    <template v-slot:counter></template>
                  </v-text-field>
                </validation-provider>
              </v-col>
              <v-col cols="12" md="6" v-if="manifest.custom_label.custom_labels6_use">
                <validation-provider
                  v-slot="{ errors }"
                  vid="custom_labels6"
                  :name="manifest.custom_label.custom_labels6_name != null ? manifest.custom_label.custom_labels6_name : 'ラベル⑥'"
                  :rules="{ required: manifest.custom_label.custom_labels6_required,
                      alpha_num_only: manifest.custom_label.custom_labels6_halfsize_alphanumeric,
                      max: manifest.custom_label.custom_labels6_maximum_characters != null ? manifest.custom_label.custom_labels6_maximum_characters : 255 ,
                      min: manifest.custom_label.custom_labels6_minimum_characters != null ? manifest.custom_label.custom_labels6_minimum_characters : 0
                      }"
                >
                  <v-text-field
                    v-model="manifest.manifest_custom_label.custom_labels_6"
                    :error-messages="errors"
                    dense
                    hide-details="auto"
                    :label="manifest.custom_label.custom_labels6_name != null ? manifest.custom_label.custom_labels6_name : 'ラベル⑥'"
                    outlined
                    persistent-placeholder
                  >
                    <template v-slot:label>
                      <span>{{ manifest.custom_label.custom_labels6_name != null ? manifest.custom_label.custom_labels6_name : 'ラベル⑥' }}</span>
                      <span class="ml-1">
                          ({{ manifest.manifest_custom_label.custom_labels_6?.length || 0 }}
                          / {{ manifest.custom_label.custom_labels6_maximum_characters != null ? manifest.custom_label.custom_labels6_maximum_characters : 255 }})
                        </span>
                    </template>
                    <template v-slot:counter></template>
                  </v-text-field>
                </validation-provider>
              </v-col>
            </v-row>
          </v-card-text>

        </template>
        <!-- /カスタムラベル -->

        <!-- 紙マニフェスト返却日 -->
        <template
          v-if="manifest.manifest_type === 'paper_form' && manifest.paper_manifest_return_vote"
        >
          <v-card-title class="pt-0">
            <span style="color: #0074BA">紙マニフェスト返却日</span>
          </v-card-title>
          <v-card-text>
            <!-- A票 -->
            <v-row>
              <v-col cols="12" md="3">
                <cs-date-picker
                  :date.sync="manifest.paper_manifest_return_vote.a_return_date"
                  @dateChangedCallback="paperManifestReturnDateAChangedCallback"
                >
                  <template v-slot="{ on, attrs }">
                    <validation-provider
                      v-slot="{ errors }"
                      name="A票　返却日"
                    >
                      <v-text-field
                        v-model="manifest.paper_manifest_return_vote.a_return_date"
                        v-bind="attrs"
                        v-on="on"
                        :error-messages="errors"
                        append-icon="mdi-calendar"
                        clearable
                        dense
                        hide-details="auto"
                        label="A票　返却日"
                        outlined
                        persistent-hint
                        persistent-placeholder
                        readonly
                      />
                    </validation-provider>
                  </template>
                </cs-date-picker>
              </v-col>
              <v-col cols="12" md="9">
                <validation-provider
                  v-slot="{ errors }"
                  name="A票　返却先"
                  rules="max:60"
                >
                  <v-text-field
                    v-model="manifest.paper_manifest_return_vote.a_return_target"
                    :error-messages="errors"
                    dense
                    hide-details="auto"
                    label="A票　返却先"
                    outlined
                    persistent-placeholder
                  >
                    <template v-slot:label>
                      <span>A票　返却先</span>
                      <span class="ml-1">
                          ({{ manifest.paper_manifest_return_vote.a_return_target?.length || 0 }} / 60)
                        </span>
                    </template>
                    <template v-slot:counter></template>
                  </v-text-field>
                </validation-provider>
              </v-col>
            </v-row>
            <!-- /A票 -->
            <!-- B2票 -->
            <v-row>
              <v-col cols="12" md="3">
                <cs-date-picker
                  :date.sync="manifest.paper_manifest_return_vote.b2_return_date"
                  @dateChangedCallback="paperManifestReturnDateB2ChangedCallback"
                >
                  <template v-slot="{ on, attrs }">
                    <validation-provider
                      v-slot="{ errors }"
                      name="B2票　返却日"
                    >
                      <v-text-field
                        v-model="manifest.paper_manifest_return_vote.b2_return_date"
                        v-bind="attrs"
                        v-on="on"
                        :error-messages="errors"
                        append-icon="mdi-calendar"
                        clearable
                        dense
                        hide-details="auto"
                        label="B2票　返却日"
                        outlined
                        persistent-hint
                        persistent-placeholder
                        readonly
                      />
                    </validation-provider>
                  </template>
                </cs-date-picker>
              </v-col>
              <v-col cols="12" md="9">
                <validation-provider
                  v-slot="{ errors }"
                  name="B2票　返却先"
                  rules="max:60"
                >
                  <v-text-field
                    v-model="manifest.paper_manifest_return_vote.b2_return_target"
                    :error-messages="errors"
                    dense
                    hide-details="auto"
                    label="B2票　返却先"
                    outlined
                    persistent-placeholder
                  >
                    <template v-slot:label>
                      <span>B2票　返却先</span>
                      <span class="ml-1">
                          ({{ manifest.paper_manifest_return_vote.b2_return_target?.length || 0 }} / 60)
                        </span>
                    </template>
                    <template v-slot:counter></template>
                  </v-text-field>
                </validation-provider>
              </v-col>
            </v-row>
            <!-- /B2票 -->
            <!-- B4票 -->
            <v-row>
              <v-col cols="12" md="3">
                <cs-date-picker
                  :date.sync="manifest.paper_manifest_return_vote.b4_return_date"
                  @dateChangedCallback="paperManifestReturnDateB4ChangedCallback"
                >
                  <template v-slot="{ on, attrs }">
                    <validation-provider
                      v-slot="{ errors }"
                      name="B4票　返却日"
                    >
                      <v-text-field
                        v-model="manifest.paper_manifest_return_vote.b4_return_date"
                        v-bind="attrs"
                        v-on="on"
                        :error-messages="errors"
                        append-icon="mdi-calendar"
                        clearable
                        dense
                        hide-details="auto"
                        label="B4票　返却日"
                        outlined
                        persistent-hint
                        persistent-placeholder
                        readonly
                      />
                    </validation-provider>
                  </template>
                </cs-date-picker>
              </v-col>
              <v-col cols="12" md="9">
                <validation-provider
                  v-slot="{ errors }"
                  name="B4票　返却先"
                  rules="max:60"
                >
                  <v-text-field
                    v-model="manifest.paper_manifest_return_vote.b4_return_target"
                    :error-messages="errors"
                    dense
                    hide-details="auto"
                    label="B4票　返却先"
                    outlined
                    persistent-placeholder
                  >
                    <template v-slot:label>
                      <span>B4票　返却先</span>
                      <span class="ml-1">
                          ({{ manifest.paper_manifest_return_vote.b4_return_target?.length || 0 }} / 60)
                        </span>
                    </template>
                    <template v-slot:counter></template>
                  </v-text-field>
                </validation-provider>
              </v-col>
            </v-row>
            <!-- /B4票 -->
            <!-- B6票 -->
            <v-row>
              <v-col cols="12" md="3">
                <cs-date-picker
                  :date.sync="manifest.paper_manifest_return_vote.b6_return_date"
                  @dateChangedCallback="paperManifestReturnDateB6ChangedCallback"
                >
                  <template v-slot="{ on, attrs }">
                    <validation-provider
                      v-slot="{ errors }"
                      name="B6票　返却日"
                    >
                      <v-text-field
                        v-model="manifest.paper_manifest_return_vote.b6_return_date"
                        v-bind="attrs"
                        v-on="on"
                        :error-messages="errors"
                        append-icon="mdi-calendar"
                        clearable
                        dense
                        hide-details="auto"
                        label="B6票　返却日"
                        outlined
                        persistent-hint
                        persistent-placeholder
                        readonly
                      />
                    </validation-provider>
                  </template>
                </cs-date-picker>
              </v-col>
              <v-col cols="12" md="9">
                <validation-provider
                  v-slot="{ errors }"
                  name="B6票　返却先"
                  rules="max:60"
                >
                  <v-text-field
                    v-model="manifest.paper_manifest_return_vote.b6_return_target"
                    :error-messages="errors"
                    dense
                    hide-details="auto"
                    label="B6票　返却先"
                    outlined
                    persistent-placeholder
                  >
                    <template v-slot:label>
                      <span>B6票　返却先</span>
                      <span class="ml-1">
                          ({{ manifest.paper_manifest_return_vote.b6_return_target?.length || 0 }} / 60)
                        </span>
                    </template>
                    <template v-slot:counter></template>
                  </v-text-field>
                </validation-provider>
              </v-col>
            </v-row>
            <!-- /B6票 -->
            <!-- D票 -->
            <v-row>
              <v-col cols="12" md="3">
                <cs-date-picker
                  :date.sync="manifest.paper_manifest_return_vote.d_return_date"
                  @dateChangedCallback="paperManifestReturnDateDChangedCallback"
                >
                  <template v-slot="{ on, attrs }">
                    <validation-provider
                      v-slot="{ errors }"
                      name="D票　返却日"
                    >
                      <v-text-field
                        v-model="manifest.paper_manifest_return_vote.d_return_date"
                        v-bind="attrs"
                        v-on="on"
                        :error-messages="errors"
                        append-icon="mdi-calendar"
                        clearable
                        dense
                        hide-details="auto"
                        label="D票　返却日"
                        outlined
                        persistent-hint
                        persistent-placeholder
                        readonly
                      />
                    </validation-provider>
                  </template>
                </cs-date-picker>
              </v-col>
              <v-col cols="12" md="9">
                <validation-provider
                  v-slot="{ errors }"
                  name="D票　返却先"
                  rules="max:60"
                >
                  <v-text-field
                    v-model="manifest.paper_manifest_return_vote.d_return_target"
                    :error-messages="errors"
                    dense
                    hide-details="auto"
                    label="D票　返却先"
                    outlined
                    persistent-placeholder
                  >
                    <template v-slot:label>
                      <span>D票　返却先</span>
                      <span class="ml-1">
                          ({{ manifest.paper_manifest_return_vote.d_return_target?.length || 0 }} / 60)
                        </span>
                    </template>
                    <template v-slot:counter></template>
                  </v-text-field>
                </validation-provider>
              </v-col>
            </v-row>
            <!-- /D票 -->
            <!-- E票 -->
            <v-row>
              <v-col cols="12" md="3">
                <cs-date-picker
                  :date.sync="manifest.paper_manifest_return_vote.e_return_date"
                  @dateChangedCallback="paperManifestReturnDateEChangedCallback"
                >
                  <template v-slot="{ on, attrs }">
                    <validation-provider
                      v-slot="{ errors }"
                      name="E票　返却日"
                    >
                      <v-text-field
                        v-model="manifest.paper_manifest_return_vote.e_return_date"
                        v-bind="attrs"
                        v-on="on"
                        :error-messages="errors"
                        append-icon="mdi-calendar"
                        clearable
                        dense
                        hide-details="auto"
                        label="E票　返却日"
                        outlined
                        persistent-hint
                        persistent-placeholder
                        readonly
                      />
                    </validation-provider>
                  </template>
                </cs-date-picker>
              </v-col>
              <v-col cols="12" md="9">
                <validation-provider
                  v-slot="{ errors }"
                  name="E票　返却先"
                  rules="max:60"
                >
                  <v-text-field
                    v-model="manifest.paper_manifest_return_vote.e_return_target"
                    :error-messages="errors"
                    dense
                    hide-details="auto"
                    label="E票　返却先"
                    outlined
                    persistent-placeholder
                  >
                    <template v-slot:label>
                      <span>E票　返却先</span>
                      <span class="ml-1">
                          ({{ manifest.paper_manifest_return_vote.e_return_target?.length || 0 }} / 60)
                        </span>
                    </template>
                    <template v-slot:counter></template>
                  </v-text-field>
                </validation-provider>
              </v-col>
            </v-row>
            <!-- /E票 -->
            <!-- B1票 -->
            <v-row>
              <v-col cols="12" md="3">
                <cs-date-picker
                  :date.sync="manifest.paper_manifest_return_vote.b1_return_date"
                  @dateChangedCallback="paperManifestReturnDateB1ChangedCallback"
                >
                  <template v-slot="{ on, attrs }">
                    <validation-provider
                      v-slot="{ errors }"
                      name="B1票　返却日"
                    >
                      <v-text-field
                        v-model="manifest.paper_manifest_return_vote.b1_return_date"
                        v-bind="attrs"
                        v-on="on"
                        :error-messages="errors"
                        append-icon="mdi-calendar"
                        clearable
                        dense
                        hide-details="auto"
                        label="B1票　返却日"
                        outlined
                        persistent-hint
                        persistent-placeholder
                        readonly
                      />
                    </validation-provider>
                  </template>
                </cs-date-picker>
              </v-col>
              <v-col cols="12" md="9">
                <validation-provider
                  v-slot="{ errors }"
                  name="B1票　返却先"
                  rules="max:60"
                >
                  <v-text-field
                    v-model="manifest.paper_manifest_return_vote.b1_return_target"
                    :error-messages="errors"
                    dense
                    hide-details="auto"
                    label="B1票　返却先"
                    outlined
                    persistent-placeholder
                  >
                    <template v-slot:label>
                      <span>B1票　返却先</span>
                      <span class="ml-1">
                          ({{ manifest.paper_manifest_return_vote.b1_return_target?.length || 0 }} / 60)
                        </span>
                    </template>
                    <template v-slot:counter></template>
                  </v-text-field>
                </validation-provider>
              </v-col>
            </v-row>
            <!-- /B1票 -->
            <!-- C1票 -->
            <v-row>
              <v-col cols="12" md="3">
                <cs-date-picker
                  :date.sync="manifest.paper_manifest_return_vote.c1_return_date"
                  @dateChangedCallback="paperManifestReturnDateC1ChangedCallback"
                >
                  <template v-slot="{ on, attrs }">
                    <validation-provider
                      v-slot="{ errors }"
                      name="C1票　返却日"
                    >
                      <v-text-field
                        v-model="manifest.paper_manifest_return_vote.c1_return_date"
                        v-bind="attrs"
                        v-on="on"
                        :error-messages="errors"
                        append-icon="mdi-calendar"
                        clearable
                        dense
                        hide-details="auto"
                        label="C1票　返却日"
                        outlined
                        persistent-hint
                        persistent-placeholder
                        readonly
                      />
                    </validation-provider>
                  </template>
                </cs-date-picker>
              </v-col>
              <v-col cols="12" md="9">
                <validation-provider
                  v-slot="{ errors }"
                  name="C1票　返却先"
                  rules="max:60"
                >
                  <v-text-field
                    v-model="manifest.paper_manifest_return_vote.c1_return_target"
                    :error-messages="errors"
                    dense
                    hide-details="auto"
                    label="C1票　返却先"
                    outlined
                    persistent-placeholder
                  >
                    <template v-slot:label>
                      <span>C1票　返却先</span>
                      <span class="ml-1">
                          ({{ manifest.paper_manifest_return_vote.c1_return_target?.length || 0 }} / 60)
                        </span>
                    </template>
                    <template v-slot:counter></template>
                  </v-text-field>
                </validation-provider>
              </v-col>
            </v-row>
            <!-- /C1票 -->
            <!-- C2票 -->
            <v-row>
              <v-col cols="12" md="3">
                <cs-date-picker
                  :date.sync="manifest.paper_manifest_return_vote.c2_return_date"
                  @dateChangedCallback="paperManifestReturnDateC2ChangedCallback"
                >
                  <template v-slot="{ on, attrs }">
                    <validation-provider
                      v-slot="{ errors }"
                      name="C2票　返却日"
                    >
                      <v-text-field
                        v-model="manifest.paper_manifest_return_vote.c2_return_date"
                        v-bind="attrs"
                        v-on="on"
                        :error-messages="errors"
                        append-icon="mdi-calendar"
                        clearable
                        dense
                        hide-details="auto"
                        label="C2票　返却日"
                        outlined
                        persistent-hint
                        persistent-placeholder
                        readonly
                      />
                    </validation-provider>
                  </template>
                </cs-date-picker>
              </v-col>
              <v-col cols="12" md="9">
                <validation-provider
                  v-slot="{ errors }"
                  name="C2票　返却先"
                  rules="max:60"
                >
                  <v-text-field
                    v-model="manifest.paper_manifest_return_vote.c2_return_target"
                    :error-messages="errors"
                    dense
                    hide-details="auto"
                    label="C2票　返却先"
                    outlined
                    persistent-placeholder
                  >
                    <template v-slot:label>
                      <span>C2票　返却先</span>
                      <span class="ml-1">
                          ({{ manifest.paper_manifest_return_vote.c2_return_target?.length || 0 }} / 60)
                        </span>
                    </template>
                    <template v-slot:counter></template>
                  </v-text-field>
                </validation-provider>
              </v-col>
            </v-row>
            <!-- /C2票 -->
          </v-card-text>
        </template>
        <!-- /紙マニフェスト返却日 -->

        <!-- マニフェスト検索キーワード -->
        <v-card-text>
          <v-row>
            <v-col cols="12" md="12">
              <validation-provider
                v-slot="{ errors }"
                name="マニフェスト検索キーワード"
                rules="max:25"
              >
                <v-text-field
                  v-model="manifest.manifest_search_keyword"
                  :error-messages="errors"
                  dense
                  hide-details="auto"
                  label="マニフェスト検索キーワード"
                  outlined
                  persistent-placeholder
                >
                  <template v-slot:label>
                    <span>マニフェスト検索キーワード</span>
                    <span class="ml-1">
                          ({{ manifest.manifest_search_keyword?.length || 0 }} / 25)
                        </span>
                    <span class="red--text font-weight-bold">&emsp;※&nbsp;DXEStation排出モードにも表示されます。&nbsp;</span>
                  </template>
                  <template v-slot:counter></template>
                </v-text-field>
              </validation-provider>
            </v-col>
          </v-row>
        </v-card-text>
        <!-- /マニフェスト検索キーワード -->

        <v-card-text class="text-right mt-2">
          引渡日: {{ formattedCollectedDate() }}&nbsp;／&nbsp;マニフェスト区分: {{ manifestTypeLabel() }}
        </v-card-text>

        <v-divider />
        <v-card-actions>
          <v-spacer />
          <v-btn
            @click="closeButtonClickEvent"
          >
            閉じる
          </v-btn>
          <VButtonProcessing
            v-if="manifest.manifest_type === 'dxe_form'"
            :onclick="reservationRegisterButtonClickEvent"
            :disabled="!manifest"
            color="primary"
            elevation="5"
          >
            予約登録
          </VButtonProcessing>
          <VButtonProcessing
            v-if="manifest.manifest_type === 'dxe_form'"
            :onclick="receptionRegisterButtonClickEvent"
            :disabled="!manifest"
            color="primary"
            elevation="5"
          >
            本登録
          </VButtonProcessing>
          <VButtonProcessing
            v-if="manifest.manifest_type !== 'dxe_form'"
            :onclick="registerButtonClickEvent"
            :disabled="!manifest"
            color="primary"
            elevation="5"
          >
            登録
          </VButtonProcessing>
        </v-card-actions>
      </v-card>
    </v-form>
  </validation-observer>
</template>

<script>
import {mapActions, mapState} from 'vuex'
import PrefectureList from '../../../../../src/mixins/PrefectureList'
import TransportRouteResult from '../../../components/TransportRouteResult'
import VButtonProcessing from '../../../../components/VButtonProcessing'
import ManifestHistory from '../managements/_ManifestHistory.vue'
import ItemAutocomplete from "@/driver/components/ItemAutocomplete.vue";

export default {
  name: 'DriverManifestLinkageNewForm',
  components: {
    VButtonProcessing,
    TransportRouteResult,
    ManifestHistory,
    ItemAutocomplete,
  },
  mixins: [
    PrefectureList
  ],
  props: {},
  data() {
    return {
      customMessages: {
        double: '数量は半角数字のみ使用できます'
      },
      manifest: null,
      searchByKeyword: null,
      manifestTypes: null,
      /** 排出事業者 (ログインしているテナントの法人) */
      supplier: null,
      /** 排出事業場 (ログインしているテナントの情報) */
      supplierLocation: null,
      supplierLocations: [],
      /** 引渡担当者・登録担当者へ登録可能ユーザ一覧 */
      handleUsers: [],
      /** 取扱品目 */
      items: [],
      /** 数量単位 */
      quantityUnits: [],
      /** 数量確定者 */
      quantityConfirms: [],
      /** 荷姿 */
      packings: [],
      /** 有害物質 */
      hazardousSubstances: [],
      /** 運搬ルート */
      transportRoutes: [],
      /** 運搬方法 */
      transportations: [],
      /** 処分方法 */
      disposalMethods: [],
      /** 収集運搬業者 */
      traders: [],
      /** 処分業者 */
      disposers: [],
      /** ドライバー */
      drivers: [],
      /** 車両 */
      vehicles: [],
      /** DXE起票担当者 */
      voteStaffs: [],
      /** 未加入業者を含むルートが選択されているか */
      includeNonSubscribeTrader: null,
      /** 排出事業場ヘッダー項目 */
      tableHeaders: [
        { value: 'name', text: '事業場名' },
        { value: 'address', text: '住所' },
        { value: 'phone_number', text: '電話番号' },
      ],
      transportRoutesTableHeaders: [
        { value: "name", text: "ルート名称", width: "25%" },
        { value: "transporter_name", text: "区間１収集運搬業者", width: "25%" },
        { value: "trader_name", text: "区間１運搬先会社名", width: "25%" },
        { value: "disposer_location_name", text: "処分業者事業場名", width: "25%" },
      ],
      displayContactNumber2: false,
      displayContactNumber3: false,
      displayNote2: false,
      displayNote3: false,
      displayNote4: false,
      displayNote5: false,
      reloadKey: 0,
    }
  },
  computed: {
    ...mapState('DriverManifestLinkage', {
      selectedItems: 'selectedItems',
    }),
    ...mapState('DriverTenantConfiguration', {
      isJwMode: 'use_jw_mode',
    }),
    disableHazardousSubstance () {
      let item = this.items.find( (v) => v.value === this.manifest.item_id)
      if (item === undefined) {
        this.removeHazardousSubstances()
        return true
      }
      if (!item.use_harmful) {
        this.removeHazardousSubstances()
      }
      return !item.use_harmful
    },
    modifyManifestTypes () {
      return this.buildManifestTypeMembers()
    },
    collectedMinimumDate () {
      if (this.manifest.manifest_type === 'dxe_form') {
        return this.$dayjs().subtract(10, 'years').format('YYYY-MM-DD')
      } else {
        return null;
      }
    },
    collectedMaximumDate () {
      if (this.manifest.manifest_type === 'dxe_form') {
        return this.$dayjs().add(5, 'years').format('YYYY-MM-DD')
      } else {
        return null;
      }
    },

  },
  watch: {
    /**
     * 運搬ルートの整合性チェック
     */
    transportRoutes() {
      this.validateTransportRoute();
    }
  },
  created() {
    this.fetchItems().then((content) => {this.items = content.items})
    this.fetchQuantityUnits().then((content) => {this.quantityUnits = content.quantity_units})
    this.fetchQuantityConfirms().then((content) => {this.quantityConfirms = content.quantity_confirms})
    this.fetchPackings().then((content) => {this.packings = content.packings})
    this.fetchHazardousSubstances().then((content) => {this.hazardousSubstances = content.hazardous_substances})
    this.fetchTransportRoutes({ without_invisible: true, first_trader_owned: false, without_self_emission: true }).then((content) => {this.transportRoutes = content.transport_routes})
    this.fetchTransportations().then((content) => {this.transportations = content.transportations})
    this.fetchDisposalMethods().then((content) => {this.disposalMethods = content.disposal_methods})
    this.fetchTraders({ search_type: 'transport', name: null }).then((content) => {this.traders = content.traders})
    this.fetchUsers({without_invisible: true}).then((content) => {this.handleUsers = content.users})
    this.fetchVoteStaffs({without_invisible: true}).then((content) => {this.voteStaffs = content.vote_staffs})
    this.fetchDrivers({without_invisible: true}).then((content) => { this.drivers = content.drivers })
    this.fetchVehicles({ registered_number: true}).then((content) => { this.vehicles = content.vehicles })

    this.fetchNewLinkage({ disposer_id: this.$route.query.disposer_id, disposer_location_code: this.$route.query.disposer_location_code }).then((content) => {
      this.manifest = content
      this.manifestTypes = content.manifest_types
      this.supplier = content.supplier
      this.supplierLocation = content.supplier_location
      this.setTransportRoutes()
    })
  },
  methods: {
    ...mapActions('snackbar', {
      showInfo: 'showInfo',
    }),
    ...mapActions('DriverManifestLinkage', {
      fetchNewLinkage: 'new',
      createLinkage: 'create',
    }),
    ...mapActions('DriverManifestLinkageReservation', {
      createLinkageReservation: 'create',
    }),
    ...mapActions('DriverManifestLinkageRegistration', {
      createLinkageRegistration: 'create',
    }),
    ...mapActions('DriverMasterSearchDriver', {
      fetchDrivers: 'index',
    }),
    ...mapActions('DriverMasterSearchVehicle', {
      fetchVehicles: 'search',
    }),
    ...mapActions('DriverMasterSearchEmissionLocation', {fetchEmissionLocations: 'index'}),
    ...mapActions('DriverMasterSearchItem', {fetchItems: 'index'}),
    ...mapActions('DriverMasterSearchPacking', {fetchPackings: 'index'}),
    ...mapActions('DriverMasterSearchHazardousSubstance', {fetchHazardousSubstances: 'index'}),
    ...mapActions('DriverMasterSearchTransportation', {fetchTransportations: 'index'}),
    ...mapActions('DriverMasterSearchQuantityUnit', {fetchQuantityUnits: 'index'}),
    ...mapActions('DriverMasterSearchDisposalMethod', {fetchDisposalMethods: 'index'}),
    ...mapActions('DriverMasterSearchQuantityConfirm', {fetchQuantityConfirms: 'index'}),
    ...mapActions('DriverMasterSearchTransportRoute', {fetchTransportRoutes: 'search'}),
    ...mapActions('DriverMasterSearchTrader', {fetchTraders: 'index'}),
    ...mapActions('DriverMasterSearchUser', {fetchUsers: 'index'}),
    ...mapActions('DriverMasterSearchVoteStaff', { fetchVoteStaffs: 'index' }),
    ...mapActions('snackbar', {showError: 'showError'}),

    /**
     * 運搬ルート行選択 イベント
     * @param item
     * @param row
     */
    transportRowClick (item, row) {
      const chosenManifestType = this.manifest.manifest_type;
      this.manifest.transport_route_id = item.id
      this.manifest.disposal_method_id = item.disposal_method_id
      row.select(true)

      // JWNET未加入業者を含む
      if (item.include_non_subscribe_trader) {
        // 紙マニフェスト区分を設定
        this.manifest.manifest_type = "paper_form";
      } else if (this.manifest.manifest_type === 'dxe_form' && item.is_self_emission && !item.self_emission_include_own) {
        // 排出自己運搬＋区間１運搬先が他社のルート
        this.manifest.manifest_type = "other_form";
      }
      this.showChangedManifestTypeMessage(chosenManifestType);
    },

    /**
     * 閉じるボタン クリックイベント
     */
    closeButtonClickEvent () {
      this.$router.push({name: 'DriverManifestLinkages', params: {resetPagination: true}})
    },

    /**
     * 受注登録ボタン クリックイベント
     */
    registerButtonClickEvent () {
      return new Promise((resolve) => {
        setTimeout(() => {
          let $self = this
          this.$refs.linkageManifestValidator.validateWithInfo().then(({ isValid, errors }) => {
            if(isValid) {
              $self.sendRegistrationInformation()
            } else {
              let messages = []
              for (let key in errors) {
                if (errors[key].length > 0) {
                  messages.push(errors[key][0])
                }
              }
              this.showError({ message: messages.join(',') })
            }
          })
          resolve();
        }, 1000);
      });
    },
    /**
     * 本登録ボタン クリックイベント
     */
    receptionRegisterButtonClickEvent () {
      return new Promise((resolve) => {
        setTimeout(() => {
          let $self = this
          this.$refs.linkageManifestValidator.validateWithInfo().then(({ isValid, errors }) => {
            if(isValid) {
              $self.sendReceptionRegistrationInformation()
            } else {
              let messages = []
              for (let key in errors) {
                if (errors[key].length > 0) {
                  messages.push(errors[key][0])
                }
              }
              this.showError({ message: messages.join(',') })
            }
          })
          resolve();
        }, 1000);
      });
    },
    /**
     * 予約登録ボタン クリックイベント
     */
    reservationRegisterButtonClickEvent () {
      return new Promise((resolve) => {
        setTimeout(() => {
          let $self = this
          this.$refs.linkageManifestValidator.validateWithInfo().then(({ isValid, errors }) => {
            if(isValid) {
              $self.sendReservationRegistrationInformation()
            } else {
              let messages = []
              for (let key in errors) {
                if (errors[key].length > 0) {
                  messages.push(errors[key][0])
                }
              }
              this.showError({ message: messages.join(',') })
            }
          })
          resolve();
        }, 1000);
      });
    },

    /**
     * JWNET取扱品目 チェンジイベント
     */
    itemChangeEvent () {
      if (this.manifest.item_id) {
        let selected = this.items.find( v => v.value === this.manifest.item_id)
        if (selected) {
          this.manifest.item_name = selected.text
        }
      }
      this.setTransportRoutes()
    },

    /**
     * 運搬担当者 チェンジイベント
     * @param item { String }
     */
    transportUserChangeEvent (item) {
      const transportUser = this.drivers.find( (v) => v.text === item)
      if (transportUser) {
        this.manifest.transport_user_id = transportUser.value
      } else {
        this.manifest.transport_user_id = null
      }
    },
    sendRegistrationInformation () {
      let params = this.buildForm()
      this.createLinkage({params: params}).then(() => {
        this.$router.push({ name: 'DriverManifestLinkages', params: { resetPagination: true, resetCheckable: true } })
      })
    },
    sendReceptionRegistrationInformation () {
      let params = this.buildForm()
      this.createLinkageRegistration({params: params}).then(() => {
        this.$router.push({ name: 'DriverManifestLinkages', params: { resetPagination: true, resetCheckable: true }})
      })
    },
    sendReservationRegistrationInformation () {
      let params = this.buildForm()
      this.createLinkageReservation({params: params}).then(() => {
        this.$router.push({name: 'DriverManifestLinkages', params: { resetPagination: true, resetCheckable: true }})
      })
    },
    /**
     * A票返却日変更　コールバック
     */
    paperManifestReturnDateAChangedCallback (value) {
      this.manifest.paper_manifest_return_vote.a_return_date = value;
    },
    /**
     * B2票返却日変更　コールバック
     */
    paperManifestReturnDateB2ChangedCallback (value) {
      this.manifest.paper_manifest_return_vote.b2_return_date = value;
    },
    /**
     * B4票返却日変更　コールバック
     */
    paperManifestReturnDateB4ChangedCallback (value) {
      this.manifest.paper_manifest_return_vote.b4_return_date = value;
    },
    /**
     * B6票返却日変更　コールバック
     */
    paperManifestReturnDateB6ChangedCallback (value) {
      this.manifest.paper_manifest_return_vote.b6_return_date = value;
    },
    /**
     * D票返却日変更　コールバック
     */
    paperManifestReturnDateDChangedCallback (value) {
      this.manifest.paper_manifest_return_vote.d_return_date = value;
    },
    /**
     * E票返却日変更　コールバック
     */
    paperManifestReturnDateEChangedCallback (value) {
      this.manifest.paper_manifest_return_vote.e_return_date = value;
    },
    /**
     * B1票返却日変更　コールバック
     */
    paperManifestReturnDateB1ChangedCallback (value) {
      this.manifest.paper_manifest_return_vote.b1_return_date = value;
    },
    /**
     * C1票返却日変更　コールバック
     */
    paperManifestReturnDateC1ChangedCallback (value) {
      this.manifest.paper_manifest_return_vote.c1_return_date = value;
    },
    /**
     * C2票返却日変更　コールバック
     */
    paperManifestReturnDateC2ChangedCallback (value) {
      this.manifest.paper_manifest_return_vote.c2_return_date = value;
    },

    /**
     * 連絡番号 追加ボタン クリックイベント
     */
    addContactNumberClick () {
      if (this.displayContactNumber2) {
        this.displayContactNumber3 = true
      } else {
        this.displayContactNumber2 = true
      }
    },
    /**
     * 備考 追加ボタン クリックイベント
     */
    addNoteClick () {
      if (this.displayNote4) {
        this.displayNote5 = true
      } else if (this.displayNote3) {
        this.displayNote4 = true
      } else if (this.displayNote2) {
        this.displayNote3 = true
      } else {
        this.displayNote2 = true
      }
    },
    /**
     * 引渡し日変更　コールバック
     */
    collectedDateChangedCallback (value) {
      this.manifest.collected_date = value;
    },
    /**
     * 回収履歴反映　コールバック
     * @param item { Object }
     */
    reflectHistoryCallback (item) {
      /**
       * 複写から除外する項目
       * ・collected_date: 回収日
       * ・manifest_no: マニフェスト番号
       * ・supplierable_id: 排出事業者ID（2次マニフェストでは固定）
       * ・supplierable_location_code: 排出事業場コード（2次マニフェストでは固定）
       * ・emission_details: 排出事業者情報
       * @type {string[]}
       */
      const excludeKeys = ['collected_date', 'manifest_no', 'emission_details', 'supplierable_id', 'supplierable_location_code']
      for (const key in item) {
        if (!excludeKeys.includes(key)) {
          this.manifest[key] = item[key]
        }
      }

      this.showInfo({message: "直近の回収履歴を反映しました"});
    },

    /**
     * 表示するマニフェスト区分の状態を更新する
     * @param manifestTypes
     * @param invalidTypes
     * @return {*}
     */
    rebuildManifestTypes(manifestTypes, invalidTypes) {
      manifestTypes.forEach((manifestType) => {
        if (invalidTypes.includes(manifestType.value)) {
          manifestType.valid = false;
        }
      })
      return manifestTypes;
    },

    /**
     * マニフェスト種別の再生成
     * @return {*|null|*[]}
     */
    buildManifestTypeMembers () {
      if (!this.manifestTypes) {
        return []
      }

      // 運搬ルート取得
      const transportRoute = this.transportRoutes.find(
        (v) => v.id === this.manifest.transport_route_id
      );

      const workManifestTypes = structuredClone(this.manifestTypes);

      // ルート選択済で未加入を含む場合は紙のみ
      if (transportRoute && transportRoute.include_non_subscribe_trader) {
        return this.rebuildManifestTypes(workManifestTypes, ['dxe_form', 'other_form']);
      }

      // EDI起票不可の場合、Web・紙のみ
      if (this.supplier.not_possible_manifest) {
        return this.rebuildManifestTypes(workManifestTypes, ['dxe_form']);
      } else if (transportRoute && transportRoute.is_self_emission && !transportRoute.self_emission_include_own) {
        // 排出自己運搬＋区間１運搬先が他社のルート
        return this.rebuildManifestTypes(workManifestTypes, ['dxe_form']);

      }
      return workManifestTypes;
    },

    /**
     * 有害物質のクリア処理
     */
    removeHazardousSubstances () {
      this.manifest.hazardous_substance_1_id = null
      this.manifest.hazardous_substance_2_id = null
      this.manifest.hazardous_substance_3_id = null
      this.manifest.hazardous_substance_4_id = null
      this.manifest.hazardous_substance_5_id = null
      this.manifest.hazardous_substance_6_id = null
    },

    /**
     * 送信パラメータの生成
     * @return { Object }
     */
    buildForm () {

      let paper_manifest_return_votes_attributes = {}
      if (this.manifest.manifest_type === 'paper_form') {
        paper_manifest_return_votes_attributes = {
          id: this.manifest.paper_manifest_return_vote.id,
          tenant_id: this.manifest.paper_manifest_return_vote.tenant_id,
          a_return_date: this.manifest.paper_manifest_return_vote.a_return_date,
          a_return_target: this.manifest.paper_manifest_return_vote.a_return_target,
          b2_return_date: this.manifest.paper_manifest_return_vote.b2_return_date,
          b2_return_target: this.manifest.paper_manifest_return_vote.b2_return_target,
          b4_return_date: this.manifest.paper_manifest_return_vote.b4_return_date,
          b4_return_target: this.manifest.paper_manifest_return_vote.b4_return_target,
          b6_return_date: this.manifest.paper_manifest_return_vote.b6_return_date,
          b6_return_target: this.manifest.paper_manifest_return_vote.b6_return_target,
          d_return_date: this.manifest.paper_manifest_return_vote.d_return_date,
          d_return_target: this.manifest.paper_manifest_return_vote.d_return_target,
          e_return_date: this.manifest.paper_manifest_return_vote.e_return_date,
          e_return_target: this.manifest.paper_manifest_return_vote.e_return_target,
          b1_return_date: this.manifest.paper_manifest_return_vote.b1_return_date,
          b1_return_target: this.manifest.paper_manifest_return_vote.b1_return_target,
          c1_return_date: this.manifest.paper_manifest_return_vote.c1_return_date,
          c1_return_target: this.manifest.paper_manifest_return_vote.c1_return_target,
          c2_return_date: this.manifest.paper_manifest_return_vote.c2_return_date,
          c2_return_target: this.manifest.paper_manifest_return_vote.c2_return_target,
        }
      }
      let manifest_custom_label_attributes = {}
      manifest_custom_label_attributes = {
        id: this.manifest.manifest_custom_label.id,
        contractor_id: this.manifest.manifest_custom_label.contractor_id,
        custom_labels_1: this.manifest.manifest_custom_label.custom_labels_1,
        custom_labels_2: this.manifest.manifest_custom_label.custom_labels_2,
        custom_labels_3: this.manifest.manifest_custom_label.custom_labels_3,
        custom_labels_4: this.manifest.manifest_custom_label.custom_labels_4,
        custom_labels_5: this.manifest.manifest_custom_label.custom_labels_5,
        custom_labels_6: this.manifest.manifest_custom_label.custom_labels_6,
      }

      return {
        manifest_type: this.manifest.manifest_type,
        dxe_vote_staff: this.manifest.dxe_vote_staff,
        supplierable_id: this.manifest.supplier.id,
        supplierable_type: this.manifest.supplier.type,
        supplierable_location_code: this.manifest.supplierable_location_code,
        deliver_staff: this.manifest.deliver_staff,
        register_staff: this.manifest.register_staff,
        collected_date: this.manifest.collected_date,
        item_type: 'Item',
        item_id: this.manifest.item_id,
        waste_name: this.manifest.waste_name,
        manifest_no: (this.manifest.manifest_type !== 'dxe_form') ? this.manifest.manifest_no : null,
        quantity: this.manifest.quantity,
        quantity_unit_id: this.manifest.quantity_unit_id,
        quantity_confirm_id: this.manifest.quantity_confirm_id,
        packing_id: this.manifest.packing_id,
        packing_quantity: this.manifest.packing_quantity,
        hazardous_substance_1_id: this.manifest.hazardous_substance_1_id,
        hazardous_substance_2_id: this.manifest.hazardous_substance_2_id,
        hazardous_substance_3_id: this.manifest.hazardous_substance_3_id,
        hazardous_substance_4_id: this.manifest.hazardous_substance_4_id,
        hazardous_substance_5_id: this.manifest.hazardous_substance_5_id,
        hazardous_substance_6_id: this.manifest.hazardous_substance_6_id,
        transport_route_id: this.manifest.transport_route_id,
        transport_user_id: (this.manifest.form_type === 'master_mode') ? this.manifest.transport_user_id : null,
        transport_user_name: this.manifest.transport_user_name,
        vehicle_number: this.manifest.vehicle_number,
        transportation_id: this.manifest.transportation_id,
        disposal_method_id: this.manifest.disposal_method_id,
        contact_number_1: this.manifest.contact_number_1,
        contact_number_2: this.manifest.contact_number_2,
        contact_number_3: this.manifest.contact_number_3,
        note_1: this.manifest.note_1,
        note_2: this.manifest.note_2,
        note_3: this.manifest.note_3,
        note_4: this.manifest.note_4,
        note_5: this.manifest.note_5,
        related_manifest_keys: this.selectedItems,
        manifest_search_keyword: this.manifest.manifest_search_keyword,
        paper_manifest_return_votes_attributes: paper_manifest_return_votes_attributes,
        manifest_custom_label_attributes: manifest_custom_label_attributes
      }
    },

    /**
     * マニフェスト区分 変更通知
     * @param oldSelectedManifestType
     */
    showChangedManifestTypeMessage (oldSelectedManifestType) {
      if (oldSelectedManifestType !== this.manifest.manifest_type) {
        const manifestType = this.manifestTypes.find( (v) => v.value === this.manifest.manifest_type)?.text;
        this.showInfo({message: `マニフェスト区分が ${manifestType} に変更されました`});
      }
    },

    /**
     * 運搬ルートの取得
     */
    setTransportRoutes () {
      this.fetchTransportRoutes({
        without_invisible: true,
        first_trader_owned: false,
        item_id: this.manifest.item_id,
        item_type: 'Item',
        without_non_reporting: true,
      }).then((content) => {
        this.transportRoutes = content.transport_routes
      })
    },

    /**
     * 運搬ルートの整合性チェック
     * 選択中の運搬ルートが有効か確認し、無効ならエラーメッセージを表示して選択を解除する
     */
    validateTransportRoute () {
      if (!this.manifest || !this.manifest.transport_route_id) return;
      const exists = this.transportRoutes.find(v => v.id === this.manifest.transport_route_id);
      if (!exists) {
        this.showError({message: '選択中のルートは対象品目外のため解除されました'});
        this.manifest.transport_route_id = null;
        this.manifest.disposal_method_id = null;
        this.manifest.includeNonSubscribeTrader = false;
        this.manifest.include_non_subscribe_trader = false;
      }
    },

    /**
     * 運搬ルート背景色設定
     * @param item
     * @return {string}
     */
    transportRouteRowBackground ( item ) {
      return (item.id === this.manifest.transport_route_id) ? 'v-data-table__selected' : ''
    },

    /**
     * フッター部 引渡日フォーマット
     * @return {string}
     */
    formattedCollectedDate() {
      if (!this.manifest.collected_date) {
        return 'ー'
      }
      return this.$dayjs(this.manifest.collected_date).format("YYYY/MM/DD");
    },

    /**
     * フッター部 マニフェスト区分名
     * @return {*}
     */
    manifestTypeLabel () {
      const manifestType = this.modifyManifestTypes.find(v => v.value === this.manifest.manifest_type);
      return manifestType?.text;
    },
  }
}
</script>

<style scoped lang="scss">
.text-small {
  font-size: .805rem !important;
}
.radio-selected {
  background-color: #dfeffd !important;
}
</style>

